import {useDispatch, useSelector} from "react-redux"
import {selectWorkspaceExclusions} from "../../../reducers/workspaceSlice"
import {selectUnassignedArticles} from "../../../reducers/unassignedArticlesEventsSlice"
import {selectWorkspaceDeletedArticles} from "../../../reducers/deletedArticlesSlice"
import {selectSessionTimestamp} from "../../../reducers/sessionTimestampSlice"
import {useEffect, useMemo, useRef, useState} from "react"
import axios from "axios"
import {
	//setDeletedArticlesAsDeleted,
	setUnassignedArticlesAsImported
} from "../../../utils/automatedSearchUtilities"
import {
	articleDeletedDuringSession,
	checkExclusion,
	getArticleTargetKey,
	updateArticleAsImported
} from "../../../utils/articleUtilities"
import PanelLoading from "../../PanelLoading"
import PanelInfiniteScroll from "../PanelInfiniteScroll"
import AutoSearchArticle from "../../articles/AutoSearchArticle"
import ImportedArticle from "../../articles/ImportedArticle"
import {
	AUTOMATED_SEARCH_PANEL_VIEW,
	incrementPageCount,
	selectAutomatedSearchPanelView,
	selectAutoSearchPage,
	selectAutoSearchResultListScrollPositionMap,
	selectImportedExcludedCount,
	setAutoSearchResultListPosition
} from "../../../reducers/automatedSearchViewSlice"
import EmptyResults from "../EmptyResults"
import {useTabPanelScrollEl} from "../../panels/TabPanel"
import {DYNAMIC_PANEL_TAB_INDEX_MAP, selectDynamicView} from "../../../reducers/dynamicViewSlice"
import {useScrollPersistence} from "../../../utils/usePersistScrollPosition"
import {useWorkspaceContext} from "../../../pages/Dashboard"

export function AutomatedSearchArticles({ selectedSearch }) {
	const dispatch = useDispatch()
	const { workspaceId } = useWorkspaceContext()
	const workspaceExcludedArticles = useSelector((state) => selectWorkspaceExclusions(state, workspaceId))
	const unassignedArticles = useSelector((state) => selectUnassignedArticles(state, workspaceId))
	const deletedArticles = useSelector((state) => selectWorkspaceDeletedArticles(state, workspaceId))
	const sessionStartTimestamp = useSelector(selectSessionTimestamp)
	const importedExcludedCount = useSelector((state) => selectImportedExcludedCount(state, workspaceId))
	const scrollPositionSelector = useMemo(() => selectAutoSearchResultListScrollPositionMap(workspaceId), [workspaceId])
	const scrollPosition = useSelector((state) => scrollPositionSelector(state, selectedSearch.id))
	const pageObjectPositionSelector = useMemo(() => selectAutoSearchPage(workspaceId), [workspaceId])
	const pageObject = useSelector((state) => pageObjectPositionSelector(state, selectedSearch.id))
	const [targetArticle, setTargetArticle] = useState(null)
	const scrollTarget = useTabPanelScrollEl()
	const displayingAutoSearchPanel = useSelector((state) => selectDynamicView(state, workspaceId)) === DYNAMIC_PANEL_TAB_INDEX_MAP.AUTOMATED_SEARCH
	const displayingArticleList = useSelector((state) => selectAutomatedSearchPanelView(state, workspaceId)) === AUTOMATED_SEARCH_PANEL_VIEW.ARTICLE_LIST
	const [searchParams, setSearchParams] = useState({
		page: 1,
		articles: [],
		count: 0,
		isLoading: true,
		hasMore: true,
	})
	const isFetchingRef = useRef(false)

	useScrollPersistence(scrollTarget.current, {
		savedPosition: scrollPosition,
		setPosition: (ratio) => dispatch(setAutoSearchResultListPosition({searchId: selectedSearch.id, position: ratio, workspaceId})),
		isActive: (displayingAutoSearchPanel && displayingArticleList),
		restoreDeps: [displayingArticleList, selectedSearch.id, searchParams.isLoading],
		restoreConditional: displayingArticleList && !searchParams.isLoading
	})

	useEffect(() => {
		fetchResults()
	}, [pageObject.page])

	useEffect(() => {
		setUnassignedArticlesAsImported(unassignedArticles, setSearchParams)
	}, [unassignedArticles])

	const paginate = () => {
		if (searchParams.hasMore && !isFetchingRef.current) {
			dispatch(incrementPageCount({selectedSearch, workspaceId}))
		}
	}

	const fetchResults = async () => {
		if(isFetchingRef.current) {return}
		isFetchingRef.current = true

		try {
			let initialPageSizeMulti = 1

			if (pageObject?.fetchInitialPage) {
				initialPageSizeMulti = pageObject.page
			}

			const { data } = await axios.get(`/api/workspace/${workspaceId}/automatedSearch/${selectedSearch.id}/results?page=${pageObject.page}`, {
				params: {sessionStartTimestamp, importedExcludedCount, pageMultiplier: initialPageSizeMulti}
			})

			setSearchParams(prevState => {
				const seen = new Set(prevState.articles.map(a => a.PMID ?? a.article.PMID))
				const incoming = data.articles.filter(a => !seen.has(a.PMID ?? a.article.PMID))
				let updateArticles = prevState.articles.concat(incoming)
				let hasMore = data.hasMore
				return {
					...prevState,
					articles: updateArticles,
					hasMore: hasMore,
					isLoading: false,
				}
			})
		} catch (error) {
			console.error(error)
			setSearchParams(prevState => { return {...prevState, isLoading: false}})
		}
		finally {
			isFetchingRef.current = false
		}
	}

	const setArticleAsImported = updateArticleAsImported(setSearchParams)

	if (searchParams.isLoading && !searchParams?.articles?.length) {
		return <PanelLoading />
	}

	if (!searchParams.isLoading && searchParams?.articles?.length == 0) {
		return <EmptyResults 
			title={`No Results in Automated Search for ${selectedSearch.title}`}
			body={"Currently, this automated search has no results. Automated searches are updated at 2 AM and 2 PM Eastern Time. Please check back later for new results."}
		/>
	}

	return (
		<PanelInfiniteScroll
			dataLength={searchParams.articles.length}
			next={paginate}
			hasMore={searchParams.hasMore}
			loader={<PanelLoading />}
			scrollableTarget={scrollTarget.current}
			fitContent
		>
			{ searchParams.articles
				.filter(article => {
					if(article?.articleId){
						const articleWasDeletedDuringSession = articleDeletedDuringSession(sessionStartTimestamp, article.deletedAt)
						if(article.deletedAt === null || articleWasDeletedDuringSession === true){
							return article
						} //else - article was deleted before session and shouldn't be rendered
					} else {
						return article
					}
				})
				.map((article, index) => {
					if(article.articleId){
						const deletedArticle = deletedArticles.find(d => d.articleId === article.articleId)
						const effectiveDeletedAt = deletedArticle?.deletedAt ?? article.deletedAt
						const articleWasDeletedDuringSession = articleDeletedDuringSession(sessionStartTimestamp, effectiveDeletedAt)

						//const articleWasDeletedDuringSession = articleDeletedDuringSession(sessionStartTimestamp, article.deletedAt)
						if(articleWasDeletedDuringSession){
							return (
								<AutoSearchArticle
									key={getArticleTargetKey(article.article)}
									workspaceId={workspaceId}
									article={article.article}
									setTargetArticle={setTargetArticle}
									targetArticle={targetArticle}
									searchId={selectedSearch.id}
									setArticleAsImported={setArticleAsImported}
									isExcluded={false}
									isDeleted={true}
									index={index}
								/>

							)
						} else {
							return (
								<ImportedArticle
									fromAutoSearch={true}
									key={article.articleId}
									articleWorkspace={article}
									setTargetArticle={setTargetArticle}
									targetArticle={targetArticle}
									isUnassigned={unassignedArticles.map(({articleId}) => articleId).includes(article.articleId)}
									index={index}
								/>
							)
						}
					} else {
						return (
							<AutoSearchArticle
								key={getArticleTargetKey(article)}
								workspaceId={workspaceId}
								article={article}
								setTargetArticle={setTargetArticle}
								targetArticle={targetArticle}
								searchId={selectedSearch.id}
								setArticleAsImported={setArticleAsImported}
								isExcluded={checkExclusion(article.PMID, workspaceExcludedArticles)}
								isDeleted={false}
								index={index}
							/>
						)
					}
				})
			}
		</PanelInfiniteScroll>
	)
}